export const STOREFRONT_PAGES = [
  { label: "Home", path: "/" },
  { label: "Shop", path: "/shop" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
  { label: "FAQ", path: "/faq" },
  { label: "Reviews", path: "/reviews" },
  { label: "Track order", path: "/track-order" },
];

export const STOREFRONT_BANNER_PAGES = STOREFRONT_PAGES.filter((page) =>
  ["/", "/shop"].includes(page.path),
);

/* ------------------------------------------------------------------ */
/* Theme catalogue                                                     */
/* ------------------------------------------------------------------ */

export const STORE_THEME_CATEGORIES = [
  { id: "all", label: "All themes" },
  { id: "versatile", label: "Versatile" },
  { id: "premium", label: "Premium" },
  { id: "fashion", label: "Fashion & beauty" },
  { id: "lifestyle", label: "Food & lifestyle" },
  { id: "tech", label: "Tech & modern" },
  { id: "bold", label: "Bold & street" },
];

export const STORE_THEMES = [
  {
    id: "modern-minimal",
    name: "Modern Minimal",
    category: "versatile",
    description:
      "An airy, product-first homepage with crisp typography, restrained borders, and generous white space.",
    tags: ["Minimal", "Image-first", "Responsive homepage"],
    designSpec: {
      palette: "White #FFFFFF, ink #172B42, blue #1559D6, slate #6B7280",
      typography: "Clean sans serif; compact labels and understated headings.",
      structure: "Split hero, category discovery, selected products, best sellers, offer, benefits, newsletter.",
      responsive: "Hero and grids collapse to a single-column editorial flow; products retain a two-column mobile grid.",
      customization: "Brand colours, fonts, logo, imagery, copy, category visibility, section order, and button shape.",
    },
  },
  {
    id: "luxury-boutique",
    name: "Luxury Boutique",
    category: "premium",
    description:
      "A refined editorial storefront with muted tones, oversized photography, and a considered luxury journey.",
    tags: ["Editorial", "Premium", "Lifestyle"],
    designSpec: {
      palette: "Oat #F5F1EB, ivory #FBF9F5, espresso #302B26, bronze #725B4B",
      typography: "Serif editorial headlines with quiet, letter-spaced sans-serif labels.",
      structure: "Image-led editorial hero, featured edit, curated categories, offer, best sellers, benefits, newsletter.",
      responsive: "Large desktop imagery gives way to stacked campaign copy and image; spacious single-column sections on mobile.",
      customization: "Merchant colours, type, logo, lifestyle imagery, hero and offer content, category visibility, and section order.",
    },
  },
  {
    id: "bold-contemporary",
    name: "Bold & Contemporary",
    category: "bold",
    description:
      "A confident, high-contrast landing page with oversized headlines, energetic accents, and visible offers.",
    tags: ["Bold", "Promotional", "High contrast"],
    designSpec: {
      palette: "Charcoal #171717, warm white #F7F6F3, signal red #E33D26, marigold #FFC247",
      typography: "Heavy, oversized sans-serif headlines and high-contrast action labels.",
      structure: "Campaign-first hero, prominent offer, category shortcuts, best sellers, featured products, benefits, newsletter.",
      responsive: "Campaign typography scales down; promotion and category modules stack without losing visual emphasis.",
      customization: "Brand palette, typography, campaign imagery and copy, promotions, category visibility, section order, and buttons.",
    },
  },
  {
    id: "fashion-lifestyle",
    name: "Fashion & Lifestyle",
    category: "fashion",
    description:
      "An image-led lookbook experience with an editorial campaign, curated categories, and a rich product edit.",
    tags: ["Fashion", "Lookbook", "Editorial"],
    designSpec: {
      palette: "Warm paper #F7F2EE, cream #FFFDFB, cocoa #2A2421, terracotta #C78567",
      typography: "Editorial serif headlines paired with restrained sans-serif utility text.",
      structure: "Lookbook campaign hero, curated categories, seasonal offer, featured edit, best sellers, benefits, newsletter.",
      responsive: "Editorial photography remains dominant; lookbook copy and grids reflow for tablet and mobile widths.",
      customization: "Merchant branding, campaign and lookbook imagery, copy, colours, type, category visibility, and content order.",
    },
  },
  {
    id: "everyday-essentials",
    name: "Everyday Essentials",
    category: "lifestyle",
    description:
      "A fast, practical shopping homepage built around category shortcuts, clear prices, and quick add-to-cart.",
    tags: ["Practical", "Quick shop", "Essentials"],
    designSpec: {
      palette: "Cool grey #F4F6F8, white #FFFFFF, navy #172B42, utility blue #1559D6, amber #F4A900",
      typography: "Highly legible sans serif with clear product names, prices, and action labels.",
      structure: "Practical hero, visible category shortcuts, best sellers, offer, featured products, benefits, newsletter.",
      responsive: "Compact hero and category shortcuts prioritize quick access; product cards keep clear prices and add actions on mobile.",
      customization: "Logo, brand colours, hero/offer media and copy, font, category visibility, section order, and button style.",
    },
  },
  {
    id: "classic",
    name: "Classic",
    isDefault: true,
    category: "versatile",
    description:
      "The current Tooraflex store experience: clear navigation, familiar product cards, trusted brand colors, and the complete standard footer.",
    tags: ["Clean", "Versatile", "Current layout"],
  },
  {
    id: "marketplace",
    name: "Marketplace",
    category: "premium",
    description:
      "A premium, editorial storefront with a dark navigation bar, warm page surfaces, roomier product cards, and a deep footer.",
    tags: ["Editorial", "Product-led", "Premium"],
  },
  {
    id: "minimal",
    name: "Minimal",
    category: "versatile",
    description:
      "A quiet, airy shop with crisp white surfaces, understated navigation, fine borders, and focus on product imagery.",
    tags: ["Airy", "Modern", "Image-first"],
  },
  {
    id: "boutique",
    name: "Boutique",
    category: "fashion",
    description:
      "A warm, expressive boutique with a plum-toned header, soft blush canvas, rounded product cards, and a rich footer.",
    tags: ["Warm", "Distinctive", "Boutique"],
  },
  {
    id: "noir",
    name: "Noir",
    category: "premium",
    description:
      "A dark, high-contrast storefront with champagne-gold details. Products glow against the page, ideal for watches, fragrance, and jewellery.",
    tags: ["Dark mode", "Luxury", "Gold accents"],
  },
  {
    id: "fresh",
    name: "Fresh",
    category: "lifestyle",
    description:
      "A bright, friendly shop in leaf green with pill buttons and generous rounded cards. Built for groceries, health food, and everyday essentials.",
    tags: ["Friendly", "Grocery", "Rounded"],
  },
  {
    id: "tech",
    name: "Voltage",
    category: "tech",
    description:
      "A crisp electronics storefront with an indigo header, cyan highlights, tight spacing, and spec-friendly product cards.",
    tags: ["Electronics", "Dense grid", "Indigo"],
  },
  {
    id: "sunset",
    name: "Sunset",
    category: "lifestyle",
    description:
      "A warm gradient header, cream canvas, and energetic orange buttons. Great for homeware, gifts, cafes, and seasonal shops.",
    tags: ["Warm", "Energetic", "Gradient header"],
  },
  {
    id: "ocean",
    name: "Ocean",
    category: "versatile",
    description:
      "Calm teal and sky blue with accent-topped cards and a deep footer. Reads as trustworthy and clean for almost any catalogue.",
    tags: ["Trustworthy", "Teal", "Accent cards"],
  },
  {
    id: "sage",
    name: "Sage",
    category: "lifestyle",
    description:
      "A natural, unhurried theme with sage greens, oat surfaces, and serif headings. Suits wellness, plants, skincare, and slow goods.",
    tags: ["Natural", "Serif", "Calm"],
  },
  {
    id: "urban",
    name: "Urban",
    category: "bold",
    description:
      "Streetwear energy: black frame, electric lime accent, heavy outlines, and offset shadows on hover.",
    tags: ["Streetwear", "High contrast", "Outlined"],
  },
  {
    id: "pastel",
    name: "Pastel",
    category: "fashion",
    description:
      "Soft lilac and mint with pill-shaped controls and very round cards. Playful but tidy, great for beauty, kids, and stationery.",
    tags: ["Playful", "Soft", "Pill buttons"],
  },
  {
    id: "heritage",
    name: "Heritage",
    category: "premium",
    description:
      "A traditional navy and burgundy look with serif type, a double-ruled header, and a formal footer for established brands.",
    tags: ["Traditional", "Serif", "Established"],
  },
  {
    id: "bold",
    name: "Bold",
    category: "bold",
    description:
      "Large type, strong red calls to action, and a wide layout that pushes promotions. Made for deal-driven stores and high volume.",
    tags: ["Promotions", "Wide", "High impact"],
  },
  {
    id: "coastal",
    name: "Coastal",
    category: "lifestyle",
    description:
      "A relaxed coastal storefront with sea-glass colors, soft borders, and an open layout for home, travel, and lifestyle shops.",
    tags: ["Coastal", "Relaxed", "Lifestyle"],
  },
  {
    id: "monochrome",
    name: "Monochrome",
    category: "premium",
    description:
      "A refined black-and-white storefront with editorial spacing and crisp product cards for modern premium brands.",
    tags: ["Minimal", "Editorial", "Black and white"],
  },
  {
    id: "artisan",
    name: "Artisan",
    category: "lifestyle",
    description:
      "A crafted, earthy storefront with warm clay accents, natural surfaces, and tactile product cards for handmade goods.",
    tags: ["Handmade", "Earthy", "Craft"],
  },
  {
    id: "wellness",
    name: "Wellness",
    category: "lifestyle",
    description:
      "A calm, spa-inspired experience with soft botanical colors, spacious sections, and gentle rounded details.",
    tags: ["Wellness", "Botanical", "Calm"],
  },
  {
    id: "studio",
    name: "Studio",
    category: "fashion",
    description:
      "A contemporary fashion studio with high-contrast type, clean monochrome surfaces, and a vivid accent color.",
    tags: ["Fashion", "Contemporary", "Editorial"],
  },
];

export const STOREFRONT_TEMPLATE_IDS = [
  ...STORE_THEMES.map((theme) => theme.id),
  "slate-executive",
  "linen-atelier",
  "forest-market",
  "coastal-clean",
  "midnight-noir",
  "terracotta-studio",
  "mono-gallery",
];

export const getStorefrontThemeMeta = (template) =>
  STORE_THEMES.find((theme) => theme.id === template) || STORE_THEMES[0];

/* ------------------------------------------------------------------ */
/* Content defaults                                                    */
/* ------------------------------------------------------------------ */

const STOREFRONT_CONTENT_DEFAULTS = {
  announcementBar: {
    enabled: true,
    text: "Complimentary delivery on qualifying orders",
    linkText: "Shop now",
    link: "/shop",
  },
  homepage: {
    heroTitle: "",
    heroDescription: "",
    heroButtonText: "",
    heroButtonLink: "",
    heroImage: "",
    promotionTitle: "",
    promotionDescription: "",
    promotionButtonText: "",
    promotionButtonLink: "/shop",
    promotionImage: "",
    showCategories: true,
    showFeaturedProducts: true,
    showBestSellers: true,
    showBenefits: true,
    showNewsletter: true,
    categoryVisibility: {},
    sectionOrder: [
      "categories",
      "featured",
      "bestsellers",
      "promotion",
      "benefits",
      "newsletter",
    ],
  },
  navigation: STOREFRONT_PAGES.slice(0, 4).map((page, index) => ({
    id: `navigation-${index}`,
    ...page,
    enabled: true,
  })),
  navigationStyle: "underline",
  navigationAnimation: "fade",
  dropdownStyle: "classic",
  categoryIntro: {
    enabled: false,
    eyebrow: "",
    title: "",
    text: "",
    buttonText: "",
    buttonLink: "/shop",
    backgroundColor: "#f1f5f9",
    textColor: "#1e293b",
    alignment: "left",
  },
  categoryLayout: {
    size: "standard",
    desktopColumns: 9,
  },
  homeLayout: {
    preset: "balanced",
    heroSize: "standard",
    categoryMenuSize: "large",
    promotionSize: "large",
    showCategoryMenu: true,
    showPromotions: true,
    showStoreTile: true,
  },
  bookingToggle: {
    homeStyle: "segmented",
    homeSize: "standard",
    shopStyle: "underline",
    shopSize: "compact",
    productLabel: "Products",
    bookingLabel: "Bookings",
    showCounts: true,
    bookingCardStyle: "standard",
  },
  footer: {
    quickLinksTitle: "Quick Links",
    supportTitle: "Support",
    contactTitle: "Contact Us",
    newsletterTitle: "Newsletter",
    newsletterText: "Subscribe to get special offers and exclusive deals.",
    copyright: "",
    showPolicies: true,
    showPayments: true,
    showSocial: true,
  },
  productGrid: {
    desktopColumns: 4,
    mobileColumns: 2,
    initialCount: 10,
    showFilters: true,
    showRandomize: true,
  },
  mobileLayout: {
    headerLayout: "menu-left-logo-center",
    menuSide: "left",
    headerBackground: "solid",
    menuTemplate: "classic",
    menuBackgroundColor: "#ffffff",
    menuTextColor: "#202223",
    menuOverlay: "dark",
    logoSize: "standard",
    heroHeight: "standard",
    heroAlignment: "left",
    categoryTabSide: "left",
    productSpacing: "comfortable",
    cardStyle: "standard",
    footerColumns: 1,
    footerStyle: "open",
  },
  animation: "fade",
  heroSlides: [],
  contentBlocks: [],
};

/* ------------------------------------------------------------------ */
/* Theme tokens                                                        */
/* ------------------------------------------------------------------ */

const STORE_THEME_CONFIGS = {
  "modern-minimal": {
    primaryColor: "#1559d6",
    accentColor: "#6b7280",
    pageBackground: "#ffffff",
    surfaceColor: "#ffffff",
    textColor: "#172b42",
    fontFamily: "sans",
    cornerStyle: "soft",
    contentWidth: "wide",
    headerStyle: "light",
    footerStyle: "dark",
  },
  "luxury-boutique": {
    primaryColor: "#725b4b",
    accentColor: "#b49a7c",
    pageBackground: "#f5f1eb",
    surfaceColor: "#fbf9f5",
    textColor: "#302b26",
    fontFamily: "serif",
    cornerStyle: "sharp",
    contentWidth: "wide",
    headerStyle: "light",
    footerStyle: "dark",
  },
  "bold-contemporary": {
    primaryColor: "#e33d26",
    accentColor: "#ffc247",
    pageBackground: "#f7f6f3",
    surfaceColor: "#ffffff",
    textColor: "#171717",
    fontFamily: "sans",
    cornerStyle: "sharp",
    contentWidth: "wide",
    headerStyle: "dark",
    footerStyle: "dark",
  },
  "fashion-lifestyle": {
    primaryColor: "#49362e",
    accentColor: "#c78567",
    pageBackground: "#f7f2ee",
    surfaceColor: "#fffdfb",
    textColor: "#2a2421",
    fontFamily: "serif",
    cornerStyle: "sharp",
    contentWidth: "wide",
    headerStyle: "light",
    footerStyle: "dark",
  },
  "everyday-essentials": {
    primaryColor: "#1559d6",
    accentColor: "#f4a900",
    pageBackground: "#f4f6f8",
    surfaceColor: "#ffffff",
    textColor: "#172b42",
    fontFamily: "sans",
    cornerStyle: "soft",
    contentWidth: "wide",
    headerStyle: "light",
    footerStyle: "dark",
  },
  classic: {
    primaryColor: "#2c7da0",
    accentColor: "#61a5c2",
    pageBackground: "#f8fafc",
    surfaceColor: "#ffffff",
    textColor: "#1e293b",
    fontFamily: "sans",
    cornerStyle: "soft",
    contentWidth: "standard",
    headerStyle: "light",
    footerStyle: "brand",
  },
  "slate-executive": {
    primaryColor: "#1e3a5f",
    accentColor: "#3b82c4",
    pageBackground: "#f4f6f9",
    surfaceColor: "#ffffff",
    textColor: "#0f1c2e",
    fontFamily: "sans",
    cornerStyle: "soft",
    contentWidth: "standard",
    headerStyle: "dark",
    footerStyle: "dark",
  },
  "linen-atelier": {
    primaryColor: "#3b2f2a",
    accentColor: "#b0714f",
    pageBackground: "#f7f3ed",
    surfaceColor: "#fffdf9",
    textColor: "#2a211d",
    fontFamily: "serif",
    cornerStyle: "sharp",
    contentWidth: "standard",
    headerStyle: "light",
    footerStyle: "brand",
  },
  "forest-market": {
    primaryColor: "#1f6b46",
    accentColor: "#8cc152",
    pageBackground: "#f3f7f3",
    surfaceColor: "#ffffff",
    textColor: "#14261c",
    fontFamily: "sans",
    cornerStyle: "rounded",
    contentWidth: "standard",
    headerStyle: "brand",
    footerStyle: "brand",
  },
  "coastal-clean": {
    primaryColor: "#0f8b8d",
    accentColor: "#ee6c4d",
    pageBackground: "#f8fbfb",
    surfaceColor: "#ffffff",
    textColor: "#10282b",
    fontFamily: "sans",
    cornerStyle: "soft",
    contentWidth: "wide",
    headerStyle: "light",
    footerStyle: "dark",
  },
  "midnight-noir": {
    primaryColor: "#c9a45c",
    accentColor: "#e7d3a3",
    pageBackground: "#16181c",
    surfaceColor: "#20232a",
    textColor: "#f1ede4",
    fontFamily: "serif",
    cornerStyle: "sharp",
    contentWidth: "wide",
    headerStyle: "dark",
    footerStyle: "dark",
  },
  "terracotta-studio": {
    primaryColor: "#b5523b",
    accentColor: "#e0a458",
    pageBackground: "#fbf5ee",
    surfaceColor: "#ffffff",
    textColor: "#33241d",
    fontFamily: "serif",
    cornerStyle: "rounded",
    contentWidth: "standard",
    headerStyle: "brand",
    footerStyle: "dark",
  },
  "mono-gallery": {
    primaryColor: "#111111",
    accentColor: "#6b6b6b",
    pageBackground: "#ffffff",
    surfaceColor: "#f6f6f6",
    textColor: "#111111",
    fontFamily: "sans",
    cornerStyle: "sharp",
    contentWidth: "wide",
    headerStyle: "light",
    footerStyle: "dark",
  },
  marketplace: {
    primaryColor: "#263a43",
    accentColor: "#a26b3f",
    pageBackground: "#f7f4ed",
    surfaceColor: "#fffdf8",
    textColor: "#202a32",
    fontFamily: "serif",
    cornerStyle: "sharp",
    contentWidth: "wide",
    headerStyle: "dark",
    footerStyle: "dark",
  },
  minimal: {
    primaryColor: "#22272b",
    accentColor: "#64748b",
    pageBackground: "#ffffff",
    surfaceColor: "#ffffff",
    textColor: "#22272b",
    fontFamily: "sans",
    cornerStyle: "sharp",
    contentWidth: "wide",
    headerStyle: "light",
    footerStyle: "light",
  },
  boutique: {
    primaryColor: "#633c58",
    accentColor: "#b77884",
    pageBackground: "#fbf5f4",
    surfaceColor: "#fffdfc",
    textColor: "#352a31",
    fontFamily: "serif",
    cornerStyle: "rounded",
    contentWidth: "standard",
    headerStyle: "brand",
    footerStyle: "brand",
  },
  noir: {
    primaryColor: "#b8893a",
    accentColor: "#e5c999",
    pageBackground: "#0e0f12",
    surfaceColor: "#181a1f",
    textColor: "#f2efe8",
    fontFamily: "serif",
    cornerStyle: "sharp",
    contentWidth: "wide",
    headerStyle: "dark",
    footerStyle: "dark",
  },
  fresh: {
    primaryColor: "#2f855a",
    accentColor: "#f6ad55",
    pageBackground: "#f6faf5",
    surfaceColor: "#ffffff",
    textColor: "#1f2d24",
    fontFamily: "sans",
    cornerStyle: "rounded",
    contentWidth: "standard",
    headerStyle: "light",
    footerStyle: "brand",
  },
  tech: {
    primaryColor: "#4f46e5",
    accentColor: "#06b6d4",
    pageBackground: "#f5f6fb",
    surfaceColor: "#ffffff",
    textColor: "#111827",
    fontFamily: "sans",
    cornerStyle: "soft",
    contentWidth: "wide",
    headerStyle: "dark",
    footerStyle: "dark",
  },
  sunset: {
    primaryColor: "#e8590c",
    accentColor: "#f59f00",
    pageBackground: "#fff8f1",
    surfaceColor: "#fffdfa",
    textColor: "#3b2418",
    fontFamily: "sans",
    cornerStyle: "rounded",
    contentWidth: "standard",
    headerStyle: "brand",
    footerStyle: "brand",
  },
  ocean: {
    primaryColor: "#0f766e",
    accentColor: "#38bdf8",
    pageBackground: "#f0f7f8",
    surfaceColor: "#ffffff",
    textColor: "#0f2a33",
    fontFamily: "sans",
    cornerStyle: "soft",
    contentWidth: "standard",
    headerStyle: "brand",
    footerStyle: "dark",
  },
  sage: {
    primaryColor: "#5f7161",
    accentColor: "#c2a878",
    pageBackground: "#f4f2ea",
    surfaceColor: "#fbfaf5",
    textColor: "#2d352e",
    fontFamily: "serif",
    cornerStyle: "soft",
    contentWidth: "standard",
    headerStyle: "light",
    footerStyle: "brand",
  },
  urban: {
    primaryColor: "#111111",
    accentColor: "#c6f432",
    pageBackground: "#f2f2f0",
    surfaceColor: "#ffffff",
    textColor: "#111111",
    fontFamily: "sans",
    cornerStyle: "sharp",
    contentWidth: "wide",
    headerStyle: "dark",
    footerStyle: "dark",
  },
  pastel: {
    primaryColor: "#8b6fd6",
    accentColor: "#7dd3b0",
    pageBackground: "#fbf9ff",
    surfaceColor: "#ffffff",
    textColor: "#34304a",
    fontFamily: "sans",
    cornerStyle: "rounded",
    contentWidth: "standard",
    headerStyle: "light",
    footerStyle: "brand",
  },
  heritage: {
    primaryColor: "#1e3a5f",
    accentColor: "#9b2c2c",
    pageBackground: "#faf7f2",
    surfaceColor: "#fffefb",
    textColor: "#1f2937",
    fontFamily: "serif",
    cornerStyle: "soft",
    contentWidth: "standard",
    headerStyle: "brand",
    footerStyle: "dark",
  },
  bold: {
    primaryColor: "#d92d20",
    accentColor: "#fdb022",
    pageBackground: "#ffffff",
    surfaceColor: "#ffffff",
    textColor: "#101828",
    fontFamily: "sans",
    cornerStyle: "soft",
    contentWidth: "wide",
    headerStyle: "light",
    footerStyle: "dark",
  },
  coastal: {
    primaryColor: "#176b75",
    accentColor: "#79c4bd",
    pageBackground: "#f1f8f7",
    surfaceColor: "#ffffff",
    textColor: "#18363b",
    fontFamily: "sans",
    cornerStyle: "rounded",
    contentWidth: "wide",
    headerStyle: "brand",
    footerStyle: "dark",
  },
  monochrome: {
    primaryColor: "#222222",
    accentColor: "#777777",
    pageBackground: "#f5f5f5",
    surfaceColor: "#ffffff",
    textColor: "#171717",
    fontFamily: "serif",
    cornerStyle: "sharp",
    contentWidth: "standard",
    headerStyle: "dark",
    footerStyle: "dark",
  },
  artisan: {
    primaryColor: "#8a4b32",
    accentColor: "#d6a77a",
    pageBackground: "#f7f1e8",
    surfaceColor: "#fffdf8",
    textColor: "#382b24",
    fontFamily: "serif",
    cornerStyle: "soft",
    contentWidth: "standard",
    headerStyle: "brand",
    footerStyle: "dark",
  },
  wellness: {
    primaryColor: "#537461",
    accentColor: "#b4c9a8",
    pageBackground: "#f4f7f1",
    surfaceColor: "#ffffff",
    textColor: "#28372e",
    fontFamily: "sans",
    cornerStyle: "rounded",
    contentWidth: "standard",
    headerStyle: "light",
    footerStyle: "brand",
  },
  studio: {
    primaryColor: "#171717",
    accentColor: "#ff5c35",
    pageBackground: "#f7f7f7",
    surfaceColor: "#ffffff",
    textColor: "#171717",
    fontFamily: "sans",
    cornerStyle: "sharp",
    contentWidth: "wide",
    headerStyle: "dark",
    footerStyle: "dark",
  },
};

export const getStorefrontThemeConfig = (template, overrides = {}) => {
  const config = {
    ...(STORE_THEME_CONFIGS[template] || STORE_THEME_CONFIGS.classic),
    ...(overrides || {}),
  };
  const validColor = (color) => /^#[0-9a-fA-F]{6}$/.test(color || "");
  const navBackgroundColor = validColor(config.navBackgroundColor)
    ? config.navBackgroundColor
    : config.headerStyle === "dark"
      ? "#17212b"
      : config.headerStyle === "brand"
        ? config.primaryColor
        : "#ffffff";
  const navLinkColor = validColor(config.navLinkColor)
    ? config.navLinkColor
    : config.headerStyle === "light"
      ? "#202a32"
      : "#ffffff";

  return {
    ...config,
    navBackgroundColor,
    navLinkColor,
    storefrontContent: {
    ...STOREFRONT_CONTENT_DEFAULTS,
    ...(overrides?.storefrontContent || {}),
    announcementBar: {
      ...STOREFRONT_CONTENT_DEFAULTS.announcementBar,
      ...(overrides?.storefrontContent?.announcementBar || {}),
    },
    homepage: {
      ...STOREFRONT_CONTENT_DEFAULTS.homepage,
      ...(overrides?.storefrontContent?.homepage || {}),
    },
    footer: {
      ...STOREFRONT_CONTENT_DEFAULTS.footer,
      ...(overrides?.storefrontContent?.footer || {}),
    },
    productGrid: {
      ...STOREFRONT_CONTENT_DEFAULTS.productGrid,
      ...(overrides?.storefrontContent?.productGrid || {}),
    },
    categoryIntro: {
      ...STOREFRONT_CONTENT_DEFAULTS.categoryIntro,
      ...(overrides?.storefrontContent?.categoryIntro || {}),
    },
    categoryLayout: {
      ...STOREFRONT_CONTENT_DEFAULTS.categoryLayout,
      ...(overrides?.storefrontContent?.categoryLayout || {}),
    },
    homeLayout: {
      ...STOREFRONT_CONTENT_DEFAULTS.homeLayout,
      ...(overrides?.storefrontContent?.homeLayout || {}),
    },
    mobileLayout: {
      ...STOREFRONT_CONTENT_DEFAULTS.mobileLayout,
      ...(overrides?.storefrontContent?.mobileLayout || {}),
    },
    navigation: Array.isArray(overrides?.storefrontContent?.navigation)
      ? overrides.storefrontContent.navigation
      : STOREFRONT_CONTENT_DEFAULTS.navigation,
    navigationStyle: [
      "underline",
      "pill",
      "minimal",
    ].includes(overrides?.storefrontContent?.navigationStyle)
      ? overrides.storefrontContent.navigationStyle
      : STOREFRONT_CONTENT_DEFAULTS.navigationStyle,
    navigationAnimation: [
      "none",
      "fade",
      "slide",
    ].includes(overrides?.storefrontContent?.navigationAnimation)
      ? overrides.storefrontContent.navigationAnimation
      : STOREFRONT_CONTENT_DEFAULTS.navigationAnimation,
    dropdownStyle: [
      "classic",
      "card",
      "minimal",
    ].includes(overrides?.storefrontContent?.dropdownStyle)
      ? overrides.storefrontContent.dropdownStyle
      : STOREFRONT_CONTENT_DEFAULTS.dropdownStyle,
    bookingToggle: {
      ...STOREFRONT_CONTENT_DEFAULTS.bookingToggle,
      ...(overrides?.storefrontContent?.bookingToggle || {}),
      homeStyle: [
        "segmented",
        "underline",
        "cards",
      ].includes(overrides?.storefrontContent?.bookingToggle?.homeStyle)
        ? overrides.storefrontContent.bookingToggle.homeStyle
        : STOREFRONT_CONTENT_DEFAULTS.bookingToggle.homeStyle,
      homeSize: ["compact", "standard", "large"].includes(
        overrides?.storefrontContent?.bookingToggle?.homeSize,
      )
        ? overrides.storefrontContent.bookingToggle.homeSize
        : STOREFRONT_CONTENT_DEFAULTS.bookingToggle.homeSize,
      shopStyle: [
        "underline",
        "soft",
        "minimal",
      ].includes(overrides?.storefrontContent?.bookingToggle?.shopStyle)
        ? overrides.storefrontContent.bookingToggle.shopStyle
        : STOREFRONT_CONTENT_DEFAULTS.bookingToggle.shopStyle,
      shopSize: ["compact", "standard", "large"].includes(
        overrides?.storefrontContent?.bookingToggle?.shopSize,
      )
        ? overrides.storefrontContent.bookingToggle.shopSize
        : STOREFRONT_CONTENT_DEFAULTS.bookingToggle.shopSize,
      bookingCardStyle: [
        "standard",
        "compact",
        "image-led",
      ].includes(overrides?.storefrontContent?.bookingToggle?.bookingCardStyle)
        ? overrides.storefrontContent.bookingToggle.bookingCardStyle
        : STOREFRONT_CONTENT_DEFAULTS.bookingToggle.bookingCardStyle,
      productLabel:
        typeof overrides?.storefrontContent?.bookingToggle?.productLabel ===
        "string"
          ? overrides.storefrontContent.bookingToggle.productLabel
          : STOREFRONT_CONTENT_DEFAULTS.bookingToggle.productLabel,
      bookingLabel:
        typeof overrides?.storefrontContent?.bookingToggle?.bookingLabel ===
        "string"
          ? overrides.storefrontContent.bookingToggle.bookingLabel
          : STOREFRONT_CONTENT_DEFAULTS.bookingToggle.bookingLabel,
      showCounts:
        typeof overrides?.storefrontContent?.bookingToggle?.showCounts ===
        "boolean"
          ? overrides.storefrontContent.bookingToggle.showCounts
          : STOREFRONT_CONTENT_DEFAULTS.bookingToggle.showCounts,
    },
    heroSlides: Array.isArray(overrides?.storefrontContent?.heroSlides)
      ? overrides.storefrontContent.heroSlides
      : [],
    contentBlocks: Array.isArray(overrides?.storefrontContent?.contentBlocks)
      ? overrides.storefrontContent.contentBlocks
      : [],
    },
  };
};
